/**
 * Vuex Store Module: UNSEEN
 * State for the cover-reveal interaction and its decoupled audio.
 *
 * Content (cover, audio, CTA) is CMS-driven and loaded via `loadAssets`.
 * There is no release phase/date logic — the CTA text/URL come straight from
 * the CMS and are swapped by admins at release.
 */

import { trackEvent } from '@/utils/mixpanel';

// A mute preference the member sets is expected to stick, so it is read back on
// load rather than resetting to unmuted every visit. Wrapped because private
// browsing and cleared site data can make localStorage throw on access, and a
// missing preference is not worth failing the module over.
const MUTE_STORAGE_KEY = 'unseen_audio_muted';

const readStoredMute = () => {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const persistMute = value => {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, String(value));
  } catch (error) {
    // Preference is best-effort; the in-memory state still applies this session.
  }
};

const state = {
  // Reveal state
  isLit: false,
  isAnimating: false,
  revealed: false,

  // Audio state
  audioEnded: false,
  isMuted: readStoredMute(),

  // Interaction tracking
  flipCount: 0,
  buttonTapCount: 0,

  // Module visibility
  isModuleVisible: false,
  moduleLoadTime: null,

  // CMS assets. The tablet/desktop key visuals are optional: when a tier is
  // empty the component falls back to `keyVisualUrl`, so the feature ships
  // whether or not all three crops have been uploaded.
  keyVisualUrl: null,
  keyVisualTabletUrl: null,
  keyVisualDesktopUrl: null,
  audioUrl: null,
  buttonText: null,
  buttonUrl: null,

  // Error handling
  lastError: null,
};

const getters = {
  isSwitchOn: state => state.isLit,
  isRevealed: state => state.revealed,
  isAnimating: state => state.isAnimating,
  hasCta: state => Boolean(state.buttonText && state.buttonUrl),
  canReplayAudio: state => Boolean(state.audioUrl && state.audioEnded),
  isMuted: state => state.isMuted,
  // The control is only meaningful once there is a track to mute.
  hasAudio: state => Boolean(state.audioUrl),
};

const mutations = {
  SET_LIT(state, value) {
    state.isLit = value;
  },

  SET_ANIMATING(state, value) {
    state.isAnimating = value;
  },

  SET_REVEALED(state, value) {
    state.revealed = value;
  },

  SET_AUDIO_ENDED(state, value) {
    state.audioEnded = value;
  },

  SET_MUTED(state, value) {
    state.isMuted = value;
  },

  INCREMENT_FLIP_COUNT(state) {
    state.flipCount += 1;
  },

  INCREMENT_BUTTON_TAP_COUNT(state) {
    state.buttonTapCount += 1;
  },

  SET_MODULE_VISIBLE(state, visible) {
    state.isModuleVisible = visible;
    if (visible && !state.moduleLoadTime) {
      state.moduleLoadTime = Date.now();
    }
  },

  SET_KEY_VISUAL_URL(state, url) {
    state.keyVisualUrl = url;
  },

  SET_KEY_VISUAL_TABLET_URL(state, url) {
    state.keyVisualTabletUrl = url;
  },

  SET_KEY_VISUAL_DESKTOP_URL(state, url) {
    state.keyVisualDesktopUrl = url;
  },

  SET_AUDIO_URL(state, url) {
    state.audioUrl = url;
  },

  SET_BUTTON_TEXT(state, text) {
    state.buttonText = text;
  },

  SET_BUTTON_URL(state, url) {
    state.buttonUrl = url;
  },

  SET_ERROR(state, error) {
    state.lastError = error;
  },

  RESET_STATE(state) {
    // isMuted is deliberately preserved: it is a member preference, not part of
    // the reveal's one-shot state.
    state.isLit = false;
    state.isAnimating = false;
    state.revealed = false;
    state.audioEnded = false;
    state.lastError = null;
  },
};

const actions = {
  /**
   * Initialize the module (record the impression once).
   */
  initializeModule({ commit, dispatch, state }) {
    try {
      if (state.isModuleVisible) return;
      commit('SET_MODULE_VISIBLE', true);
      dispatch('recordModuleView');
    } catch (error) {
      console.error('Error initializing UNSEEN module:', error);
      commit('SET_ERROR', error.message);
    }
  },

  /**
   * Reveal the cover. One-shot: once revealed, it does not run again.
   */
  revealCover({ commit, state, dispatch }) {
    if (state.revealed || state.isAnimating) return;
    try {
      commit('SET_ANIMATING', true);
      commit('SET_LIT', true);
      commit('SET_REVEALED', true);
      dispatch('recordFlip', { newState: true });
    } catch (error) {
      console.error('Error revealing UNSEEN cover:', error);
      commit('SET_ERROR', error.message);
    }
  },

  toggleSwitch({ commit, state, dispatch }) {
    const newState = !state.isLit;
    commit('SET_ANIMATING', false);
    commit('SET_LIT', newState);
    commit('SET_REVEALED', state.revealed || newState);
    commit('SET_AUDIO_ENDED', false);
    dispatch('recordFlip', { newState });
  },

  /**
   * Animation lock toggle (component releases it once the reveal settles).
   */
  setAnimating({ commit }, value) {
    commit('SET_ANIMATING', value);
  },

  setAudioEnded({ commit }, value) {
    commit('SET_AUDIO_ENDED', value);
  },

  /**
   * Mute/unmute the teaser. The track keeps playing while muted so its position
   * stays honest -- unmuting drops the member back into the song where it has
   * got to, rather than restarting it.
   */
  toggleMute({ commit, state }) {
    const next = !state.isMuted;
    commit('SET_MUTED', next);
    persistMute(next);
    try {
      trackEvent('unseen_audio_mute_toggle', {
        muted: next,
      });
    } catch (error) {
      console.error('Error recording UNSEEN mute toggle:', error);
    }
  },

  recordModuleView() {
    try {
      trackEvent('unseen_module_view');
    } catch (error) {
      console.error('Error recording module view:', error);
    }
  },

  recordFlip({ commit, state }, { newState }) {
    try {
      commit('INCREMENT_FLIP_COUNT');
      trackEvent('unseen_switch_flip', {
        new_state: newState ? 'on' : 'off',
        flip_count: state.flipCount,
      });
    } catch (error) {
      console.error('Error recording flip:', error);
    }
  },

  recordButtonTap({ commit, state }) {
    try {
      commit('INCREMENT_BUTTON_TAP_COUNT');
      trackEvent('unseen_button_tap', {
        label: state.buttonText,
        url: state.buttonUrl,
        tap_count: state.buttonTapCount,
      });
    } catch (error) {
      console.error('Error recording button tap:', error);
    }
  },

  recordAudioPlay(_ctx, { isReplay = false } = {}) {
    try {
      trackEvent('unseen_audio_play', {
        is_replay: isReplay,
      });
    } catch (error) {
      console.error('Error recording audio play:', error);
    }
  },

  /**
   * Load CMS assets into the module.
   */
  loadAssets(
    { commit },
    { keyVisualUrl, keyVisualTabletUrl, keyVisualDesktopUrl, audioUrl, buttonText, buttonUrl } = {},
  ) {
    try {
      commit('SET_KEY_VISUAL_URL', keyVisualUrl || null);
      commit('SET_KEY_VISUAL_TABLET_URL', keyVisualTabletUrl || null);
      commit('SET_KEY_VISUAL_DESKTOP_URL', keyVisualDesktopUrl || null);
      commit('SET_AUDIO_URL', audioUrl || null);
      // No hardcoded fallbacks. These defaulted to 'Pre-save on Spotify' and
      // the Spotify homepage, which made `hasCta` permanently true -- so the
      // button rendered even when an admin had deliberately left the fields
      // blank, and an asset-less surface showed a live CTA pointing at nothing
      // in particular. The plan's acceptance criteria require the opposite:
      // "Button hidden when either field empty."
      commit('SET_BUTTON_TEXT', buttonText || null);
      commit('SET_BUTTON_URL', buttonUrl || null);
    } catch (error) {
      console.error('Error loading UNSEEN assets:', error);
      commit('SET_ERROR', error.message);
    }
  },

  resetToDark({ commit }) {
    commit('RESET_STATE');
  },

  clearError({ commit }) {
    commit('SET_ERROR', null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
